import { useState, useEffect, useCallback, useRef } from 'react';
import { FiUsers, FiSearch, FiFilter, FiMoreVertical, FiUserCheck, FiUserX, FiShield } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { getAdminUsers, updateUserStatus, updateUserRole } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import './AdminUsers.css';

const ROLES = ['all', 'buyer', 'vendor', 'admin'];
const STATUS_OPTIONS = ['all', 'active', 'inactive'];

const ROLE_BADGE = {
  buyer:  'badge-success',
  vendor: 'badge-primary',
  admin:  'badge-danger',
};

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const menuRef = useRef(null);
  const LIMIT = 20;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (search.trim()) params.search = search.trim();
      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active';
      const { data } = await getAdminUsers(params);
      setUsers(data.users || []);
      setTotal(data.pagination?.total || 0);
      setPages(data.pagination?.pages || 1);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, statusFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleToggleStatus = async (user) => {
    setActionLoading(user._id + '-status');
    setOpenMenuId(null);
    try {
      const { data } = await updateUserStatus(user._id, { isActive: !user.isActive });
      setUsers((prev) => prev.map((u) => u._id === user._id ? data.user : u));
      toast.success(`User ${data.user.isActive ? 'activated' : 'deactivated'}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRoleChange = async (user, newRole) => {
    if (newRole === user.role) { setOpenMenuId(null); return; }
    setActionLoading(user._id + '-role');
    setOpenMenuId(null);
    try {
      const { data } = await updateUserRole(user._id, { role: newRole });
      setUsers((prev) => prev.map((u) => u._id === user._id ? data.user : u));
      toast.success(`Role updated to ${newRole}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <AdminLayout title="Users" action={
      <span className="au-total-badge">{total.toLocaleString()} total</span>
    }>
      <div className="au-page">
        {/* Filters */}
        <div className="au-filters card">
          <div className="card-body" style={{ padding: '1rem 1.25rem' }}>
            <div className="au-filter-row">
              <form className="au-search-wrap" onSubmit={handleSearchSubmit}>
                <FiSearch className="au-search-icon" />
                <input
                  type="text"
                  className="form-input au-search-input"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />
              </form>

              <div className="au-filter-group">
                <FiFilter size={14} />
                <select
                  className="form-select au-select"
                  value={roleFilter}
                  onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{r === 'all' ? 'All Roles' : r.charAt(0).toUpperCase() + r.slice(1)}</option>
                  ))}
                </select>

                <select
                  className="form-select au-select"
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s === 'all' ? 'All Status' : s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><FiUsers size={40} /></div>
            <h3>No users found</h3>
            <p>Try adjusting your search or filters.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isLoading = actionLoading?.startsWith(user._id);
                  const initials = user.name?.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase() || '?';
                  return (
                    <tr key={user._id} style={{ opacity: isLoading ? 0.6 : 1 }}>
                      <td>
                        <div className="au-user-cell">
                          {user.avatar
                            ? <img src={`http://localhost:5000${user.avatar}`} alt={user.name} className="avatar avatar-sm" />
                            : <span className="avatar avatar-sm">{initials}</span>}
                          <div>
                            <p className="au-user-name">{user.name}</p>
                            <p className="au-user-email">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${ROLE_BADGE[user.role] || 'badge-info'}`}>{user.role}</span>
                      </td>
                      <td>
                        <span className={`badge ${user.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-muted text-sm">{fmtDate(user.createdAt)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="au-actions" ref={openMenuId === user._id ? menuRef : null}>
                          <button
                            className="btn btn-ghost btn-sm au-menu-btn"
                            onClick={() => setOpenMenuId(openMenuId === user._id ? null : user._id)}
                            disabled={isLoading}
                          >
                            {isLoading ? <span className="spinner spinner-sm" /> : <FiMoreVertical size={16} />}
                          </button>

                          {openMenuId === user._id && (
                            <div className="au-dropdown">
                              <button
                                className="au-dropdown-item"
                                onClick={() => handleToggleStatus(user)}
                              >
                                {user.isActive ? <><FiUserX size={14} /> Deactivate</> : <><FiUserCheck size={14} /> Activate</>}
                              </button>

                              <div className="au-dropdown-divider" />
                              <p className="au-dropdown-label">Change Role</p>
                              {['buyer', 'vendor', 'admin'].filter((r) => r !== user.role).map((r) => (
                                <button
                                  key={r}
                                  className="au-dropdown-item"
                                  onClick={() => handleRoleChange(user, r)}
                                >
                                  <FiShield size={14} />
                                  Make {r.charAt(0).toUpperCase() + r.slice(1)}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="au-pagination">
            <button
              className="btn btn-ghost btn-sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              ← Previous
            </button>
            <span className="au-page-info">Page {page} of {pages}</span>
            <button
              className="btn btn-ghost btn-sm"
              disabled={page === pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
